import React, { useState, useMemo } from 'react';

import styled, { css } from 'styled-components';

import { useUserPermission } from '@contexts/UserContext';

import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import FlexContainer from '@components/Gui/FlexContainer';
import { NwEditButton } from '@components/Gui/NwButton';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwIcon from '@components/Gui/NwIcon';
import SocialsDrawer from '@components/Social/SocialsDrawer';
import { getContactType } from '@components/Social/SocialUtil';

import ContactSocialLink from './ContactSocialLink';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Container = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
`;

const SocialsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;

    /* Su mobile le spaziature pensate per il desktop (padding del link e margine dell'icona)
       sono eccessive: le azzero e uso un gap unico, piu' compatto su entrambi gli assi. */
    ${(props) =>
        props.$mobile &&
        css`
            gap: 1rem 1.5rem;

            .contact-social-link {
                padding-right: 0;
            }

            .icon-container {
                margin-right: 0;
            }
        `}
`;

const LockContainer = styled(FlexContainer)`
    svg {
        font-size: 1.6rem;
    }
`;

const ContactSocials = ({ currentContact, previewMode, minheight }) => {
    const [isOpenAddEditSocials, setIsOpenAddEditSocials] = useState(false);
    const breakpoints = useNwBreakPoints();
    const contactType = currentContact ? getContactType(currentContact.SubType) : undefined;

    const viewClientSocials = useUserPermission(
        'ViewClientSocials',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const viewAgencySocials = useUserPermission(
        'ViewAgencySocials',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const viewServiceSocials = useUserPermission(
        'ViewServiceSocials',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const viewModelSocials = useUserPermission(
        'ViewModelSocials',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );

    const viewSocials = useMemo(() => {
        if (currentContact) {
            if (currentContact.SubType === 1) return viewClientSocials;
            if (currentContact.SubType === 2) return viewAgencySocials;
            if (currentContact.SubType === 3) return viewServiceSocials;
            return viewModelSocials;
        }
        return false;
    }, [currentContact, viewClientSocials, viewAgencySocials, viewServiceSocials, viewModelSocials]);

    const handleOpenAddEditSocialsDrawer = () => {
        setIsOpenAddEditSocials(true);
    };

    const handleCloseAddEditSocialsDrawer = () => {
        setIsOpenAddEditSocials(false);
    };

    const renderSocials = () => {
        if (!viewSocials) {
            return (
                <LockContainer justifyContent="center" alignItems="center">
                    <NwIcon icon={byPrefixAndName.fasl['lock-alt']} />
                </LockContainer>
            );
        }

        return (
            <SocialsContainer className="contact-socials-links" $mobile={breakpoints === 1}>
                {currentContact.Socials.map((social, index) => (
                    <ContactSocialLink contactSocial={social} contactType={contactType} key={index} />
                ))}
            </SocialsContainer>
        );
    };

    return breakpoints === 1 ? (
        renderSocials()
    ) : (
        <NwCard title="Socials" icon={byPrefixAndName.fasl['hashtag']} horizontal>
            <Container>
                {renderSocials()}
                {!previewMode && viewSocials && (
                    <NwEditButton disabled={!viewSocials} ghost size="small" onClick={handleOpenAddEditSocialsDrawer} />
                )}
            </Container>
            {isOpenAddEditSocials && (
                <SocialsDrawer contactID={currentContact.ID} contactType={contactType} onClose={handleCloseAddEditSocialsDrawer} />
            )}
        </NwCard>
    );
};

export default ContactSocials;
